
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';



/* ────────────────────────────────────────────────────────────────────
   Time-of-day greeting + lightweight speech helpers.
   Mirrors the voice pattern already used in ChatScreen.jsx / useVoiceChat.js
   (speak a bot line, show a 🔊 icon, mic ⇄ send toggle, allow stop) but
   kept dependency-free so this section works even where useVoiceChat
   isn't wired up yet.
──────────────────────────────────────────────────────────────────── */
export const getTimeGreeting = (date = new Date()) => {
  const hour = date.getHours();
  if (hour < 5) return 'Hello!';
  if (hour < 12) return 'Good morning!';
  if (hour < 17) return 'Good afternoon!';
  if (hour < 21) return 'Good evening!';
  return 'Hello!';
};

const pickVoice = () => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  const female = ['female', 'woman', 'samantha', 'google us english', 'zira', 'aditi', 'veena'];
  const scored = voices
    .filter((v) => v.lang?.toLowerCase().startsWith('en'))
    .map((v) => ({
      voice: v,
      score:
        (female.some((k) => v.name.toLowerCase().includes(k)) ? 20 : 0) +
        (v.name.toLowerCase().includes('google') ? 10 : 0) +
        (v.lang.toLowerCase().includes('in') ? 5 : 0),
    }))
    .sort((a, b) => b.score - a.score);
  return scored[0]?.voice || voices[0];
};

const SpeechRecognitionCtor =
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;
const isSpeechSupported = !!SpeechRecognitionCtor;

/* ────────────────────────────────────────────────────────────────────
   Route intents — maps things a person might say/type to an actual
   app route, most-specific first. Sourced from AppRoutes.jsx.
──────────────────────────────────────────────────────────────────── */
const ROUTE_INTENTS = [
  { route: '/pregnancy-screening', label: 'Pregnancy Care', keywords: ['pregnan', 'expecting a baby', 'planning a baby'] },
  { route: '/pcos-screening', label: 'PCOS Assessment', keywords: ['pcos', 'pcod', 'hormonal concern', 'hormone imbalance'] },
  { route: '/wellness-screening', label: 'Mental Wellness Support', keywords: ['mental wellness', 'mental health', 'anxiety', 'stress', 'depress'] },
  { route: '/menopause-screening', label: 'Menopause Support', keywords: ['menopause'] },
  { route: '/symptom-checker', label: 'Symptom Checker', keywords: ['symptom', 'not feeling well', 'check my symptoms'] },
  { route: '/our-doctors', label: 'Our Doctors', keywords: ['find a doctor', 'find doctor', 'gynecologist', 'specialist', 'our doctors'] },
  { route: '/book-appointment', label: 'Book Appointment', keywords: ['book an appointment', 'book appointment', 'book a doctor', 'schedule a visit', 'appointment'] },
  { route: '/health-assessment', label: 'Health Assessment', keywords: ['health assessment', 'take an assessment'] },
  { route: '/care-navigator', label: 'Care Navigator', keywords: ['care navigator', 'ai navigator', 'talk to navigator'] },
  { route: '/findprogram', label: 'Find the Right Program', keywords: ['find a program', 'find program', 'right program for me'] },
  { route: '/programs', label: 'Health Programs', keywords: ['health programs', 'programs'] },
  { route: '/conditions', label: 'Health Concerns', keywords: ['conditions', 'health concerns'] },
  { route: '/lifeStages', label: 'Life Stages', keywords: ['life stages', 'life stage', 'every stage of life'] },
  { route: '/women-health-record', label: 'Health Record', keywords: ['health record', 'my records', 'medical record'] },
  { route: '/patientExperience', label: 'Patient Experience', keywords: ['patient experience', 'testimonials', 'reviews', 'success stories'] },
  { route: '/carejourney', label: 'Care Journey', keywords: ['care journey', 'my journey'] },
  { route: '/resource', label: 'Resources', keywords: ['resources', 'articles', 'blog'] },
  { route: '/services', label: 'Services', keywords: ['services', 'what services'] },
  { route: '/about', label: 'About Us', keywords: ['about us', 'about the clinic', 'who are you'] },
  { route: '/register', label: 'Sign Up', keywords: ['sign up', 'create account', 'register'] },
  { route: '/sign-in', label: 'Sign In', keywords: ['sign in', 'log in', 'login'] },
];

const matchRouteIntent = (text) => {
  if (!text) return null;
  const t = text.toLowerCase();
  return ROUTE_INTENTS.find((intent) => intent.keywords.some((k) => t.includes(k))) || null;
};

// Short tap-to-send starters, shown as a "Try asking" row above the input —
// separate from the fuller quickReplies list further up the chat.
const DEFAULT_SAMPLE_PROMPTS = [
  'What could cause irregular periods?',
  'Help me find a gynecologist near me',
  'I want to book an appointment',
  'What should I expect at my first visit?',
];


/* ────────────────────────────────────────────────────────────────────
   Small inline icon set (kept dependency-free, matches the hand-rolled
   SVG pattern already used in HeroSection.jsx / KeyServices.jsx)
──────────────────────────────────────────────────────────────────── */
const Icon = ({ name, className = '', size = 20, color = 'currentColor' }) => {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: color,
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    className,
  };
  switch (name) {
    case 'sparkles':
      return (
        <svg {...common}>
          <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6z" />
          <path d="M19 15l.7 1.9L21.5 17.6l-1.8.7L19 20.2l-.7-1.9-1.8-.7 1.8-.7z" />
        </svg>
      );
    case 'stethoscope':
      return (
        <svg {...common}>
          <path d="M5 4v6a4 4 0 008 0V4" />
          <path d="M9 15a5 5 0 0010 0v-2" />
          <circle cx="19" cy="8" r="2" />
        </svg>
      );
    case 'doctor':
      return (
        <svg {...common}>
          <circle cx="12" cy="7" r="3.2" />
          <path d="M5 21c1.2-4 3.8-6 7-6s5.8 2 7 6" />
          <path d="M12 12.5V16" />
        </svg>
      );
    case 'baby':
      return (
        <svg {...common}>
          <circle cx="12" cy="7.5" r="3.3" />
          <path d="M6.5 20c0-4 2.2-6.5 5.5-6.5S17.5 16 17.5 20" />
          <path d="M9.5 7.2c0-1.3.8-2 1.3-2.6" />
        </svg>
      );
    case 'hormone':
      return (
        <svg {...common}>
          <circle cx="9" cy="9" r="4.2" />
          <circle cx="16" cy="16" r="3" />
          <path d="M12.2 11.8L13.7 13.3" />
        </svg>
      );
    case 'brain':
      return (
        <svg {...common}>
          <path d="M9 4a3 3 0 00-3 3v.3A3 3 0 004 10a3 3 0 002 2.8V15a3 3 0 003 3h1V4z" />
          <path d="M15 4a3 3 0 013 3v.3a3 3 0 012 2.7 3 3 0 01-2 2.8V15a3 3 0 01-3 3h-1V4z" />
        </svg>
      );
    case 'search':
      return (
        <svg {...common}>
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
      );
    case 'send':
      return (
        <svg {...common} fill={color} stroke="none">
          <path d="M3 12l18-8-8 18-2.5-7.5L3 12z" />
        </svg>
      );
    case 'mic':
      return (
        <svg {...common}>
          <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
          <path d="M19 10v2a7 7 0 01-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </svg>
      );
    case 'chevronRight':
      return (
        <svg {...common}>
          <polyline points="9 6 15 12 9 18" />
        </svg>
      );
    case 'volume':
      return (
        <svg {...common}>
          <polygon points="4 9 8 9 12 5 12 19 8 15 4 15 4 9" />
          <path d="M16.5 8.5a5 5 0 010 7" />
          <path d="M19 6a8.5 8.5 0 010 12" />
        </svg>
      );
    case 'volumeOff':
      return (
        <svg {...common}>
          <polygon points="4 9 8 9 12 5 12 19 8 15 4 15 4 9" />
          <line x1="17" y1="9" x2="22" y2="14" />
          <line x1="22" y1="9" x2="17" y2="14" />
        </svg>
      );
    case 'close':
      return (
        <svg {...common}>
          <line x1="5" y1="5" x2="19" y2="19" />
          <line x1="19" y1="5" x2="5" y2="19" />
        </svg>
      );
    case 'chat':
      return (
        <svg {...common}>
          <path d="M21 12a8 8 0 01-11.8 7L3 21l2.1-6.2A8 8 0 1121 12z" />
        </svg>
      );
    case 'insight':
      return (
        <svg {...common}>
          <path d="M12 3v3M5.6 5.6l2.1 2.1M3 12h3M5.6 18.4l2.1-2.1M12 18a5 5 0 100-10 5 5 0 000 10z" />
          <path d="M10 21h4" />
        </svg>
      );
    case 'target':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="12" cy="12" r="0.6" fill={color} />
        </svg>
      );
    case 'check':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <polyline points="8.5 12.5 11 15 16 9" />
        </svg>
      );
    case 'refresh':
      return (
        <svg {...common}>
          <path d="M3 12a9 9 0 0115.4-6.4L21 8" />
          <path d="M21 3v5h-5" />
          <path d="M21 12a9 9 0 01-15.4 6.4L3 16" />
          <path d="M3 21v-5h5" />
        </svg>
      );
    case 'clipboard':
      return (
        <svg {...common}>
          <rect x="5" y="4" width="14" height="17" rx="2" />
          <path d="M9 4V3a1 1 0 011-1h4a1 1 0 011 1v1" />
          <line x1="9" y1="11" x2="15" y2="11" />
          <line x1="9" y1="15" x2="15" y2="15" />
        </svg>
      );
    case 'chartLine':
      return (
        <svg {...common}>
          <path d="M4 19V5" />
          <path d="M4 19h16" />
          <path d="M7 15l3-4 3 2 4-6" />
        </svg>
      );
    case 'alert':
      return (
        <svg {...common}>
          <path d="M12 3l9.5 17H2.5z" />
          <line x1="12" y1="10" x2="12" y2="14.5" />
          <circle cx="12" cy="17.2" r="0.6" fill={color} />
        </svg>
      );
    case 'shield':
      return (
        <svg {...common}>
          <path d="M12 3l7 3v6c0 4.6-3 7.6-7 9-4-1.4-7-4.4-7-9V6z" />
        </svg>
      );
    case 'lock':
      return (
        <svg {...common}>
          <rect x="5" y="11" width="14" height="9" rx="2" />
          <path d="M8 11V7a4 4 0 018 0v4" />
        </svg>
      );
    case 'users':
      return (
        <svg {...common}>
          <circle cx="9" cy="8" r="3" />
          <path d="M3 20c.8-3.2 3-5 6-5s5.2 1.8 6 5" />
          <circle cx="17.5" cy="9" r="2.3" />
          <path d="M15 13.3c2.6.3 4.2 2 4.8 4.7" />
        </svg>
      );
    case 'heart':
      return (
        <svg {...common}>
          <path d="M12 20s-7-4.35-9.33-8.86C1.2 8 2.6 4.5 6 4.5c2 0 3.4 1.2 6 3.6 2.6-2.4 4-3.6 6-3.6 3.4 0 4.8 3.5 3.33 6.64C19 15.65 12 20 12 20z" />
        </svg>
      );
    default:
      return null;
  }
};

/* ────────────────────────────────────────────────────────────────────
   1. HERO — "Healthcare Begins With a Conversation"
      Left: headline + trust row. Right: photo layered behind the AI
      chat card + quick-action rail. Uses var(--primary-color) /
      var(--secondary-color) exactly as the rest of the codebase does.
──────────────────────────────────────────────────────────────────── */
const DEFAULT_QUICK_REPLIES = [
  { icon: 'stethoscope', tint: '#FCE7EA', accent: '#E0536B', label: 'I want to check my symptoms', sub: 'Get AI-powered symptom insights' },
  { icon: 'doctor', tint: '#EDE9FE', accent: 'var(--primary-color)', label: 'I need to book a doctor', sub: 'Find the right specialist' },
  { icon: 'baby', tint: '#FFEFE0', accent: '#E8863A', label: "I'm pregnant or planning pregnancy", sub: 'Personalized care for you' },
  { icon: 'hormone', tint: '#E4F6EC', accent: '#2FA872', label: 'I have PCOS or hormonal concerns', sub: 'Track, manage and improve' },
  { icon: 'brain', tint: '#E4EEFC', accent: '#3E7FD9', label: 'I need mental wellness support', sub: "We're here to support you" },
];

const DEFAULT_RAIL = [
  { icon: 'stethoscope', label: 'Symptoms Check', color: '#E0536B', href: '/symptom-checker' },
  { icon: 'doctor', label: 'Find Doctor', color: 'var(--primary-color)', href: '/doctors' },
  { icon: 'clipboard', label: 'Book Appointment', color: '#3E7FD9', href: '/book-appointment' },
  { icon: 'heart', label: 'Health Programs', color: '#2FA872', href: '/programs' },
  // { icon: 'chartLine', label: 'Track Progress', color: '#3E7FD9', href: '/progress' },
  { icon: 'alert', label: 'Emergency Help', color: '#D9432E', href: '/emergency' },
];

const TRUST_ROW = [
  { icon: 'shield', label: 'Clinically Validated' },
  { icon: 'lock', label: 'Secure & Private' },
  // { icon: 'sparkles', label: 'AI-Powered Insights' },
  { icon: 'users', label: 'Trusted by Experts' },
];



export const AIHeroSection = ({
  eyebrow = 'AI-Powered. Human-Centered. Always Here.',
  headingStrong = 'Healthcare Begins With a',
  headingAccent = 'Conversation.',
  description = 'Our AI Care Navigator understands you, guides you to the right care, and helps you through every step of your health journey.',
  image,
  imageAlt = 'Woman using the Femcare AI Care Navigator on her phone',
  primaryCta = { label: 'Start a Conversation', action: 'start-conversation' },
  secondaryCta = { label: 'Explore Services', href: '/services' },
  quickReplies = DEFAULT_QUICK_REPLIES,
  rail = DEFAULT_RAIL,
  samplePrompts = DEFAULT_SAMPLE_PROMPTS,
  onQuickReply,
  autoSpeakGreeting = true,
}) => {
  const navigate = useNavigate();

  // Dynamic, time-of-day-aware greeting — "Good morning! I'm your AI Care
  // Navigator. How can I help you today?" (computed once per page load).
  const [welcomeText] = useState(
    () => `${getTimeGreeting()} I'm your AI Care Navigator. How can I help you today?`
  );

  const [messages, setMessages] = useState([{ from: 'bot', text: welcomeText }]);
  const [draft, setDraft] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const [shouldSpeakGreeting, setShouldSpeakGreeting] = useState(false);

useEffect(() => {
  setShouldSpeakGreeting(true);
}, []);

  useEffect(() => {
  if (!shouldSpeakGreeting) return;

  speak(welcomeText);
}, [shouldSpeakGreeting, welcomeText]);


  /* ── Speech OUTPUT (bot speaks) ───────────────────────────── */
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceReady, setVoiceReady] = useState(false);
  const hasAutoSpokenRef = useRef(false);

  const stopSpeaking = () => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
  };

  
  const speak = (text, onComplete) => {
    if (
      typeof window === "undefined" ||
      !window.speechSynthesis ||
      !text?.trim()
    ) {
      onComplete?.();
      return;
    }

    // Stop any previous speech
    window.speechSynthesis.cancel();

    const utter = new SpeechSynthesisUtterance(text);

    const voice = pickVoice();
    if (voice) utter.voice = voice;

    utter.lang = voice?.lang || "en-IN";
    utter.rate = 0.98;
    utter.pitch = 1.05;

    utter.onstart = () => {
      setIsSpeaking(true);
    };

    utter.onend = () => {
      setIsSpeaking(false);
      onComplete?.();
    };

    utter.onerror = () => {
      setIsSpeaking(false);
      onComplete?.();
    };

    window.speechSynthesis.speak(utter);
  };

  // Warm up the voice list (Chrome loads voices async).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const mark = () => setVoiceReady(true);
    mark();
    window.speechSynthesis.onvoiceschanged = mark;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
      stopSpeaking();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-speak the greeting once, as soon as the page loads. Most browsers
  // require a prior user gesture before allowing audio autoplay, so this is
  // a best-effort attempt — the speaker icon on the bubble lets the person
  // trigger it manually if the browser blocks the automatic attempt.
  useEffect(() => {
    if (!autoSpeakGreeting || hasAutoSpokenRef.current) return;
    hasAutoSpokenRef.current = true;
    const timer = setTimeout(() => {
      try {
        speak(welcomeText);
      } catch {
        /* autoplay blocked — user can tap the speaker icon instead */
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceReady]);

  /* ── Speech INPUT (mic → live transcript → auto-send) ────────
     Same shape as ChatScreen.jsx + useVoiceChat.js: tapping the mic
     starts listening, live words show in the input, and stopping
     (manually or via the send button while listening) sends whatever
     was captured. Input shows a mic icon while empty, and swaps to a
     send icon the moment there's text — exactly like ChatScreen. */
  const [isListening, setIsListening] = useState(false);
  const [liveText, setLiveText] = useState('');
  const [micError, setMicError] = useState('');
  const recognizerRef = useRef(null);
  const finalBufferRef = useRef('');
  const silenceTimerRef = useRef(null);

  const stopListening = (andSend = false) => {
    clearTimeout(silenceTimerRef.current);
    if (recognizerRef.current) {
      recognizerRef.current._stopped = true;
      recognizerRef.current.onend = null;
      try { recognizerRef.current.stop(); } catch { /* noop */ }
      recognizerRef.current = null;
    }
    setIsListening(false);
    if (andSend) {
      const text = finalBufferRef.current.trim();
      finalBufferRef.current = '';
      setLiveText('');
      if (text) handleSend(null, text);
    }
  };

  const resetSilenceTimer = () => {
    clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = setTimeout(() => {
      if (finalBufferRef.current.trim()) stopListening(true);
    }, 1600);
  };

  const startListening = () => {
    if (!isSpeechSupported) return;
    stopSpeaking();
    setMicError('');
    finalBufferRef.current = '';
    setLiveText('');

    const rec = new SpeechRecognitionCtor();
    rec.lang = 'en-IN';
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onstart = () => setIsListening(true);

    rec.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += chunk + ' ';
        else interim += chunk;
      }
      if (final) {
        finalBufferRef.current += final;
        resetSilenceTimer();
      }
      if (interim) resetSilenceTimer();
      setLiveText((finalBufferRef.current + interim).trim());
    };

    rec.onerror = (e) => {
      if (e.error === 'no-speech') return;
      setMicError(e.error === 'not-allowed' ? 'Microphone permission denied.' : `Error: ${e.error}`);
      setIsListening(false);
    };

    rec.onend = () => {
      if (rec._stopped) return;
      try { rec.start(); } catch { /* noop */ }
    };

    recognizerRef.current = rec;
    try { rec.start(); } catch { setIsListening(false); }
  };

  const toggleMic = () => (isListening ? stopListening(true) : startListening());

  useEffect(() => () => stopListening(false), []); // cleanup on unmount

  const hasText = isListening ? liveText.trim().length > 0 : draft.trim().length > 0;

  /* ── Send + route-intent extraction ──────────────────────────
     Whatever the person says or types is checked against a list of
     known app routes. If we find a match ("book appointment",
     "PCOS", "menopause", "find a doctor", ...) the bot confirms it
     out loud and then navigates there automatically. */
  const respondTo = (text) => {
    const intent = matchRouteIntent(text);

    setIsTyping(true);

    setTimeout(() => {
      setIsTyping(false);

      const replyText = intent
        ? `Sure! Taking you to ${intent.label} now...`
        : "Thanks for sharing that. I'll connect you with the right care option — you can also try one of the suggestions above.";

      setMessages((prev) => [
        ...prev,
        {
          from: "bot",
          text: replyText,
        },
      ]);

      stopSpeaking();

      if (intent) {
        // Speak first, then navigate
        speak(replyText, () => {
          navigate(intent.route);
        });
      } else {
        speak(replyText);
      }
    }, 650);
  };

  const handleSend = (e, overrideText) => {
    e?.preventDefault?.();
    stopSpeaking();
    const text = (overrideText ?? draft).trim();
    if (!text) return;
    setMessages((prev) => [...prev, { from: 'user', text }]);
    setDraft('');
    respondTo(text);
  };



  const handleQuickReply = (item) => {
  stopSpeaking();

  setMessages((prev) => [
    ...prev,
    {
      from: "user",
      text: item.label,
    },
  ]);

  if (onQuickReply) {
    onQuickReply(item);
  }

  respondTo(item.route ? `${item.label} ${item.route}` : item.label);
};

  const handlePrimaryCta = () => {
    if (primaryCta.action === 'start-conversation') {
      document.getElementById('ai-chat-input')?.focus();
      return;
    }
    navigate(primaryCta.href || '/book-appointment');
  };

  const handleRailClick = (r) => {
    stopSpeaking();
    if (r.route) navigate(r.route);
  };

  return (
    <section
      id="hero"
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: 'var(--ava-color-primary-50)' }}
    >
      <style>{`
        @keyframes aiTypingBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        .ai-typing-dot {
          width: 6px; height: 6px; border-radius: 999px;
          background: var(--primary-color);
          animation: aiTypingBounce 1.1s ease-in-out infinite;
        }
        .ai-typing-dot:nth-child(2) { animation-delay: 0.15s; }
        .ai-typing-dot:nth-child(3) { animation-delay: 0.3s; }
      `}</style>

      <div className="container mx-auto px-4 md:px-8 pt-8 pb-10 md:pt-12 md:pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-8 lg:gap-10 items-start">

          {/* ── Left column ───────────────────────────────── */}
          <div className="flex flex-col items-start">
            {eyebrow && (
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                style={{ backgroundColor: 'rgba(255,255,255,0.7)', border: '1px solid var(--gray-200, #e5e7eb)' }}
              >
                <Icon name="sparkles" size={14} color="var(--primary-color)" />
                <span className="ava-text-xs font-medium" style={{ color: 'var(--text-dark)' }}>{eyebrow}</span>
              </div>
            )}

            <h1 className="ava-heading-xl mb-4">
              <span className="block" style={{ color: 'var(--text-dark)' }}>{headingStrong}</span>
              <span className="block" style={{ color: 'var(--secondary-color)' }}>{headingAccent}</span>
            </h1>

            <p className="ava-text-body mb-7" style={{ maxWidth: 480 }}>{description}</p>

            <div className="flex flex-wrap items-center gap-3 mb-7">
              <button className="btn-primary" onClick={handlePrimaryCta}>
                <span className="inline-flex items-center gap-2">
                  <Icon name="chat" size={16} color="currentColor" />
                  {primaryCta.label}
                </span>
              </button>
              <button
                className="btn-transparent"
                onClick={() => navigate(secondaryCta.href || '/services')}
              >
                {secondaryCta.label}
              </button>
            </div>

            {image && (
              <div className="relative w-full max-w-[420px] mx-auto lg:mx-0" style={{ aspectRatio: '4/3.4' }}>
                <div
                  className="absolute inset-0 rounded-[2rem]"
                  style={{ backgroundColor: 'rgba(224,83,107,0.12)' }}
                />
                <img
                  src={image}
                  alt={imageAlt}
                  className="relative w-full h-full object-cover object-top rounded-[2rem]"
                  draggable={false}
                />
              </div>
            )}

            {/* Trust row */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-2">
              {TRUST_ROW.map((t) => (
                <div key={t.label} className="flex items-center gap-2">
                  <Icon name={t.icon} size={16} color="var(--primary-color)" />
                  <span className="ava-text-xs font-medium" style={{ color: 'var(--text-light)' }}>{t.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right column: chat card + rail ───────────── */}
          <div className="flex items-start gap-3 w-full">
            <div
              className="flex-1 bg-white ava-radius-md overflow-hidden"
              style={{ boxShadow: '0 12px 40px rgba(15,15,40,0.10)', border: '1px solid var(--gray-200, #e5e7eb)' }}
            >
              {/* Chat header */}
              <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--gray-200, #e5e7eb)' }}>
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--primary-color)' }}
                  >
                    <Icon name="sparkles" size={17} color="#fff" />
                  </div>
                  <div>
                    <div className="ava-text-subheading" style={{ lineHeight: 1.1 }}>AI Care Navigator</div>
                    <div className="ava-text-xs" style={{ color: 'var(--text-light)' }}>Your personal health assistant</div>
                  </div>
                </div>
                {isSpeaking ? (
                  <button onClick={stopSpeaking} className="flex items-center gap-1.5" aria-label="Stop speaking">
                    <Icon name="volume" size={13} color="var(--primary-color)" />
                    <span className="ava-text-xs font-medium" style={{ color: 'var(--primary-color)' }}>Speaking…</span>
                    <Icon name="close" size={11} color="var(--text-light)" />
                  </button>
                ) : isListening ? (
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#D9432E' }} />
                    <span className="ava-text-xs font-medium" style={{ color: '#D9432E' }}>Listening…</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#2FA872' }} />
                    <span className="ava-text-xs font-medium" style={{ color: '#2FA872' }}>Online</span>
                  </div>
                )}
              </div>

              {/* Chat body */}
              <div className="px-5 py-4 flex flex-col gap-3" style={{ maxHeight: 320, overflowY: 'auto' }}>
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className="flex items-end gap-2"
                    style={{ alignSelf: m.from === 'bot' ? 'flex-start' : 'flex-end', maxWidth: '85%' }}
                  >
                    <div
                      className="ava-text-sm px-3.5 py-2.5 rounded-xl"
                      style={
                        m.from === 'bot'
                          ? { backgroundColor: 'var(--ava-color-primary-50)', color: 'var(--text-dark)' }
                          : { backgroundColor: 'var(--primary-color)', color: '#fff' }
                      }
                    >
                      {m.text}
                    </div>
                    {m.from === 'bot' && (
                      <button
                        onClick={() => (isSpeaking ? stopSpeaking() : speak(m.text))}
                        aria-label={isSpeaking ? 'Stop reading message aloud' : 'Read message aloud'}
                        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ border: '1px solid var(--gray-200, #e5e7eb)' }}
                      >
                        <Icon name={isSpeaking ? 'volumeOff' : 'volume'} size={13} color="var(--primary-color)" />
                      </button>
                    )}
                  </div>
                ))}

                {isTyping && (
                  <div
                    className="flex items-center gap-1.5 px-3.5 py-3 rounded-xl"
                    style={{ backgroundColor: 'var(--ava-color-primary-50)', alignSelf: 'flex-start' }}
                  >
                    <span className="ai-typing-dot" />
                    <span className="ai-typing-dot" />
                    <span className="ai-typing-dot" />
                  </div>
                )}

                {/* Quick replies — shown until the conversation actually starts */}
                {messages.length <= 1 &&
                  quickReplies.map((item) => (
                    <button
                      key={item.label}
                      onClick={() => handleQuickReply(item)}
                      className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-left transition-colors hover:brightness-95"
                      style={{ border: '1px solid var(--gray-200, #e5e7eb)' }}
                    >
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: item.tint }}
                      >
                        <Icon name={item.icon} size={16} color={item.accent} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="ava-text-sm font-medium truncate" style={{ color: 'var(--text-dark)' }}>{item.label}</div>
                        <div className="ava-text-xs truncate" style={{ color: 'var(--text-light)' }}>{item.sub}</div>
                      </div>
                      <Icon name="chevronRight" size={16} color="var(--text-light)" />
                    </button>
                  ))}
              </div>

              {/* Sample conversation starters */}
              {/* {samplePrompts?.length > 0 && (
                <div className="px-5 pb-3 flex flex-wrap gap-2" style={{ borderTop: '1px solid var(--gray-200, #e5e7eb)', paddingTop: 12 }}>
                  <span className="ava-text-xs font-medium w-full mb-0.5" style={{ color: 'var(--text-light)' }}>Try asking</span>
                  {samplePrompts.map((prompt) => (
                    <button
                      key={prompt}
                      onClick={() => handleSend(null, prompt)}
                      className="px-3 py-1.5 rounded-full ava-text-xs font-medium transition-colors hover:brightness-95"
                      style={{ backgroundColor: 'var(--ava-color-primary-50)', color: 'var(--text-dark)' }}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              )} */}

              {/* Chat input — mic icon while empty, send icon once there's text
                  (typed OR spoken), exactly like ChatScreen.jsx's smart toggle */}
              <div className="px-5 pb-2 pt-1">
                <form
                  onSubmit={handleSend}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl"
                  style={{ border: `1.5px solid ${isListening ? 'var(--primary-color)' : 'var(--gray-200, #e5e7eb)'}` }}
                >
                  <input
                    id="ai-chat-input"
                    type="text"
                    value={isListening ? liveText : draft}
                    onChange={(e) => !isListening && setDraft(e.target.value)}
                    placeholder={isListening ? 'Listening…' : 'Type your message...'}
                    readOnly={isListening}
                    className="flex-1 outline-none bg-transparent ava-text-sm"
                    style={{ color: 'var(--text-dark)', minWidth: 0 }}
                  />

                  {!hasText && !isListening ? (
                    <button
                      type="button"
                      onClick={toggleMic}
                      disabled={!isSpeechSupported}
                      aria-label="Speak your message"
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'var(--ava-color-primary-50)', opacity: isSpeechSupported ? 1 : 0.4 }}
                    >
                      <Icon name="mic" size={14} color="var(--primary-color)" />
                    </button>
                  ) : (
                    <button
                      type={isListening ? 'button' : 'submit'}
                      onClick={() => isListening && toggleMic()}
                      aria-label="Send message"
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'var(--primary-color)' }}
                    >
                      <Icon name="send" size={14} color="#fff" />
                    </button>
                  )}
                </form>

                {isListening && (
                  <div className="ava-text-xs mt-1.5 flex items-center gap-1.5" style={{ color: 'var(--primary-color)' }}>
                    <Icon name="mic" size={11} color="var(--primary-color)" /> Listening — pause when you're done speaking
                  </div>
                )}
                {micError && (
                  <div className="ava-text-xs mt-1.5" style={{ color: '#D9432E' }}>{micError}</div>
                )}

                <p className="ava-text-xs mt-2 text-center" style={{ color: 'var(--text-light)' }}>
                  This is not a substitute for medical advice.
                </p>
              </div>
            </div>

            {/* Quick-action rail */}
            <div className="hidden lg:flex flex-col gap-2 bg-white ava-radius-md p-2" style={{ border: '1px solid var(--gray-200, #e5e7eb)' }}>
              {rail.map((r) => (
                <button
                  key={r.label}
                  onClick={() => handleRailClick(r)}
                  className="flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl transition-colors hover:bg-[var(--ava-color-primary-50)]"
                  style={{ width: 84 }}
                >
                  <Icon name={r.icon} size={18} color={r.color} />
                  <span className="ava-text-xs font-medium text-center" style={{ color: 'var(--text-dark)', lineHeight: 1.15 }}>{r.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────
   HeroTextBlock — unchanged
───────────────────────────────────────────── */
const HeroTextBlock = ({
  eyebrow,
  headingStrong,
  headingAccent,
  description,
  primaryCta,
  secondaryCta,
  handlePrimaryCta,
  navigate,
}) => (
  <>
    {eyebrow && (
      <div
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
        style={{ backgroundColor: 'rgba(255,255,255,0.7)', border: '1px solid var(--gray-200, #e5e7eb)' }}
      >
        <Icon name="sparkles" size={14} color="var(--primary-color)" />
        <span className="ava-text-xs font-medium" style={{ color: 'var(--text-dark)' }}>{eyebrow}</span>
      </div>
    )}

    {(headingStrong || headingAccent) && (
      <h1 className="ava-heading-xl">
        {headingStrong && (
          <span className="block" style={{ color: 'var(--text-dark)' }}>{headingStrong}</span>
        )}
        {headingAccent && (
          <span className="block" style={{ color: 'var(--secondary-color)' }}>{headingAccent}</span>
        )}
      </h1>
    )}

    {description && (
      <p className="ava-text-body" style={{ maxWidth: 480 }}>{description}</p>
    )}

    {(primaryCta || secondaryCta) && (
      <div className="flex flex-wrap items-center gap-3">
        {primaryCta && (
          <button className="btn-primary" onClick={handlePrimaryCta}>
            <span className="inline-flex items-center gap-2">
              <Icon name="chat" size={16} color="currentColor" />
              {primaryCta.label}
            </span>
          </button>
        )}
        {secondaryCta && (
          <button
            className="btn-transparent"
            onClick={() => navigate(secondaryCta.href || '/services')}
          >
            {secondaryCta.label}
          </button>
        )}
      </div>
    )}
  </>
);

/* ─────────────────────────────────────────────
   ChatCardWithRail
   Card takes the remaining space (flex-1, min-w-0 so it can
   actually shrink), Rail is a fluid-width column that scales
   between clamp(56px, 18%, 84px) — narrow but never hidden.
───────────────────────────────────────────── */
const ChatCardWithRail = ({
  messages,
  quickReplies,
  handleQuickReply,
  draft,
  setDraft,
  handleSend,
  isChatOpen,
  setIsChatOpen,
  rail,
  handleRailClick,
  className = '',
  compact = false,
  mirrored = false,
}) => {
  const Rail = (
    <div
      className="flex flex-col gap-2 shrink-0 h-full justify-evenly bg-white ava-radius-md p-2"
      style={{ border: '1px solid var(--gray-200, #e5e7eb)', width: 'clamp(56px, 18%, 84px)' }}
    >
      {rail.map((r) => (
        <button
          key={r.label}
          onClick={() => handleRailClick(r)}
          className="flex flex-col items-center gap-1.5 px-1.5 py-2 rounded-xl transition-colors hover:bg-[var(--ava-color-primary-50)] w-full min-w-0"
        >
          <Icon name={r.icon} size={17} color={r.color} />
          <span className="ava-text-xs font-medium text-center" style={{ color: 'var(--text-dark)', lineHeight: 1.15 }}>{r.label}</span>
        </button>
      ))}
    </div>
  );

  const Card = (
    <div
      className={`flex-1 h-full min-w-0 flex flex-col bg-white ava-radius-md overflow-hidden ${isChatOpen ? '' : 'invisible'}`}
      style={{ boxShadow: '0 12px 40px rgba(15,15,40,0.10)', border: '1px solid var(--gray-200, #e5e7eb)' }}
    >
      {/* Chat header — shrink-0 so it never gets squeezed by the flexible body below */}
      <div className="flex items-center justify-between px-5 py-3.5 shrink-0" style={{ borderBottom: '1px solid var(--gray-200, #e5e7eb)' }}>
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'var(--primary-color)' }}
          >
            <Icon name="sparkles" size={17} color="#fff" />
          </div>
          <div className="min-w-0">
            <div className="ava-text-subheading truncate" style={{ lineHeight: 1.1 }}>AI Care Navigator</div>
            <div className="ava-text-xs truncate" style={{ color: 'var(--text-light)' }}>Your personal health assistant</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setIsChatOpen(false)}
          aria-label="Close AI Care Navigator"
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors hover:bg-[var(--gray-100)]"
        >
          <Icon name="close" size={16} color="var(--text-light)" />
        </button>
      </div>

      {/* Chat body — flex-1 fills whatever height the wrapper gives it
          (driven by top/bottom % on the outer box), min-h-0 is what
          actually lets a flex child shrink and scroll instead of
          overflowing its container */}
      <div className="px-5 py-3.5 flex flex-col gap-2.5 flex-1 min-h-0 overflow-y-auto">
        {messages.map((m, i) => (
          <div
            key={i}
            className="ava-text-sm px-3.5 py-2.5 rounded-xl"
            style={
              m.from === 'bot'
                ? { backgroundColor: 'var(--ava-color-primary-50)', color: 'var(--text-dark)', alignSelf: 'flex-start', maxWidth: '85%' }
                : { backgroundColor: 'var(--primary-color)', color: '#fff', alignSelf: 'flex-end', maxWidth: '85%' }
            }
          >
            {m.text}
          </div>
        ))}

        {quickReplies.map((item) => (
          <button
            key={item.label}
            onClick={() => handleQuickReply(item)}
            className="w-full flex items-center gap-3 px-3.5 py-2 rounded-xl text-left transition-colors hover:brightness-95"
            style={{ border: '1px solid var(--gray-200, #e5e7eb)' }}
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: item.tint }}
            >
              <Icon name={item.icon} size={15} color={item.accent} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="ava-text-sm font-medium truncate" style={{ color: 'var(--text-dark)' }}>{item.label}</div>
              <div className="ava-text-xs truncate" style={{ color: 'var(--text-light)' }}>{item.sub}</div>
            </div>
            <Icon name="chevronRight" size={15} color="var(--text-light)" />
          </button>
        ))}
      </div>

      {/* Chat input — shrink-0 so it stays pinned at the bottom */}
      <div className="px-5 pb-3.5 pt-1 shrink-0">
        <form
          onSubmit={handleSend}
          className="flex items-center gap-2 px-3 py-2 rounded-xl"
          style={{ border: '1.5px solid var(--gray-200, #e5e7eb)' }}
        >
          <input
            id="ai-chat-input"
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 outline-none bg-transparent ava-text-sm"
            style={{ color: 'var(--text-dark)', minWidth: 0 }}
          />
          <button
            type="submit"
            aria-label="Send message"
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'var(--primary-color)' }}
          >
            <Icon name="send" size={14} color="#fff" />
          </button>
        </form>
        <p className="ava-text-xs mt-2 text-center" style={{ color: 'var(--text-light)' }}>
          This is not a substitute for medical advice.
        </p>
      </div>
    </div>
  );

  return (
    <div style={{ gap: '4%' }} className={`flex items-start ${className}`}>
      {Card}
      {Rail}
    </div>
  );
};

/* ─────────────────────────────────────────────
   TrustRowBlock
───────────────────────────────────────────── */
const TrustRowBlock = () => (
  <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-1">
    {TRUST_ROW.map((t) => (
      <div key={t.label} className="flex items-center gap-2">
        <Icon name={t.icon} size={16} color="var(--primary-color)" />
        <span className="ava-text-xs font-medium" style={{ color: 'var(--text-light)' }}>{t.label}</span>
      </div>
    ))}
  </div>
);
/* ─────────────────────────────────────────────
   TrustRowBlock
───────────────────────────────────────────── */
// const TrustRowBlock = () => (
//   <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-1">
//     {TRUST_ROW.map((t) => (
//       <div key={t.label} className="flex items-center gap-2">
//         <Icon name={t.icon} size={16} color="var(--primary-color)" />
//         <span className="ava-text-xs font-medium" style={{ color: 'var(--text-light)' }}>{t.label}</span>
//       </div>
//     ))}
//   </div>
// );
/* ────────────────────────────────────────────────────────────────────
   2. POPULAR CONVERSATIONS — horizontal scroll of chip prompts
──────────────────────────────────────────────────────────────────── */
const DEFAULT_CONVERSATIONS = [
  { icon: 'doctor', color: '#E0536B', tint: '#FCE7EA', label: 'I have irregular periods' },
  { icon: 'doctor', color: 'var(--primary-color)', tint: '#EDE9FE', label: 'I need a gynecologist' },
  { icon: 'baby', color: '#E8863A', tint: '#FFEFE0', label: 'Planning pregnancy' },
  { icon: 'hormone', color: '#2FA872', tint: '#E4F6EC', label: 'PCOS symptoms' },
  { icon: 'heart', color: '#D9432E', tint: '#FBE4E1', label: 'Heavy bleeding' },
  { icon: 'brain', color: '#3E7FD9', tint: '#E4EEFC', label: 'Pelvic pain' },
];

export const PopularConversations = ({ items = DEFAULT_CONVERSATIONS, onSelect, viewAllHref = '/search' }) => {
  const navigate = useNavigate();
  const scrollerRef = useRef(null);

  const scrollByChip = () => {
    scrollerRef.current?.scrollBy({ left: 220, behavior: 'smooth' });
  };

  const handleClick = (item) => {
    if (onSelect) return onSelect(item);
    navigate(`/search?q=${encodeURIComponent(item.label)}`);
  };

  return (
    <section className="w-full bg-white py-6 md:py-8">
      <div className="container mx-auto px-4 md:px-8">
        <div
          className="ava-radius-md px-5 md:px-6 py-5 md:py-6"
          style={{ backgroundColor: 'var(--gray-100)' }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="ava-text-subheading">Common Conversations</h3>
            {/* <button onClick={() => navigate(viewAllHref)} className="ava-text-sm font-semibold inline-flex items-center gap-1" style={{ color: 'var(--primary-color)' }}>
              View all <Icon name="chevronRight" size={14} color="var(--primary-color)" />
            </button> */}
          </div>

          <div className="flex items-center gap-2">
            <div ref={scrollerRef} className="flex gap-3 overflow-x-auto no-scrollbar" style={{ scrollbarWidth: 'none' }}>
              {items.map((item) => (
                <button
                  key={item.label}
                  onClick={() => handleClick(item)}
                  className="flex items-center gap-2.5 pl-2 pr-4 py-2 rounded-full bg-white flex-shrink-0 transition-colors hover:brightness-95"
                  style={{ border: '1px solid var(--gray-200, #e5e7eb)', cursor: 'default' }}
                >
                  <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: item.tint }}>
                    <Icon name={item.icon} size={15} color={item.color} />
                  </span>
                  <span className="ava-text-sm font-medium whitespace-nowrap" style={{ color: 'var(--text-dark)' }}>"{item.label}"</span>
                </button>
              ))}
            </div>
            <button
              onClick={scrollByChip}
              aria-label="Scroll for more"
              className="hidden md:flex w-9 h-9 rounded-full items-center justify-center flex-shrink-0 bg-white"
              style={{ border: '1px solid var(--gray-200, #e5e7eb)' }}
            >
              <Icon name="chevronRight" size={16} color="var(--text-dark)" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ────────────────────────────────────────────────────────────────────
   3. CARE JOURNEY — 6-step connected timeline
──────────────────────────────────────────────────────────────────── */
const DEFAULT_JOURNEY = [
  { icon: 'chat', title: 'You Talk', desc: 'Share your concerns in simple words' },
  { icon: 'sparkles', title: 'AI Understands', desc: 'We analyze and identify what matters' },
  { icon: 'insight', title: 'Personalized Insights', desc: 'Get insights and risk assessment' },
  { icon: 'target', title: 'Find Right Care', desc: 'We match you with the right specialists' },
  { icon: 'check', title: 'Take Action', desc: 'Book appointment or start a program' },
  { icon: 'refresh', title: 'Follow Up', desc: 'Track progress and stay healthy' },
];

export const CareJourney = ({ heading = 'Your Care Journey with FemCare', steps = DEFAULT_JOURNEY }) => (
  <section className="w-full py-8 md:py-10" style={{ backgroundColor: 'var(--ava-color-primary-50)' }}>
    <div className="container mx-auto px-4 md:px-8">
      <h2 className="ava-heading-lg text-center mb-8">{heading}</h2>

      <div className="flex items-start justify-between overflow-x-auto no-scrollbar" style={{ gap: 4 }}>
        {steps.map((step, i) => (
          <React.Fragment key={step.title}>
            <div className="flex flex-col items-center flex-shrink-0" style={{ width: 130 }}>
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mb-3 bg-white"
                style={{ border: '2px solid var(--primary-color)' }}
              >
                <Icon name={step.icon} size={22} color="var(--primary-color)" />
              </div>
              <div className="ava-text-sm font-semibold text-center" style={{ color: 'var(--text-dark)' }}>{step.title}</div>
              <div className="ava-text-xs text-center mt-1" style={{ color: 'var(--text-light)' }}>{step.desc}</div>
            </div>
            {i < steps.length - 1 && (
              <div className="flex items-center flex-shrink-0" style={{ marginTop: 27, minWidth: 24 }}>
                <div style={{ width: 24, height: 0, borderTop: '2px dashed var(--gray-200, #d8d8e0)' }} />
                <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: 'var(--gray-200, #d8d8e0)', marginLeft: -2 }} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  </section>
);

/* ────────────────────────────────────────────────────────────────────
   4. LIFE STAGES — "Care for Every Stage of Life"
──────────────────────────────────────────────────────────────────── */
const DEFAULT_STAGES = [
  { icon: 'baby', color: 'var(--primary-color)', tint: '#EDE9FE', title: 'Adolescence', desc: 'Learn. Grow. Thrive.', image: '/Home/adolescence.svg' },
  { icon: 'hormone', color: '#E0536B', tint: '#FCE7EA', title: 'Fertility & Preconception', desc: 'Plan today for a healthier tomorrow.', image: '/Home/fetility.svg' },
  { icon: 'baby', color: '#E8863A', tint: '#FFEFE0', title: 'Pregnancy', desc: 'Nurturing you, nurturing life.', image: '/Home/pregnancy.svg' },
  { icon: 'heart', color: '#2FA872', tint: '#E4F6EC', title: 'Mother Care', desc: 'Care for you and your little one.', image: '/Home/mother_care.webp' },
  { icon: 'brain', color: 'var(--primary-color)', tint: '#EDE9FE', title: 'Menopause', desc: 'Embrace this new chapter with confidence.', image: '/Home/menopause_cropped.png' },
  { icon: 'shield', color: 'var(--primary-color)', tint: '#E4EEFC', title: 'Healthy Aging', desc: 'Healthy today, stronger tomorrow.', image: '/Home/Rectangle 532_csropped.png' },
];

export const LifeStages = ({ heading = 'Care for Every Stage of Life', stages = DEFAULT_STAGES }) => {
  const navigate = useNavigate();
  return (
    <section className="w-full bg-white py-8 md:py-10">
      <div className="container mx-auto px-4 md:px-8">
        <h2 className="ava-heading-lg text-center mb-6">{heading}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 ava-card-gap">
          {stages.map((s) => (
            <button
              key={s.title}
              onClick={() => navigate(`/search?q=${encodeURIComponent(s.title)}`)}
              className="flex flex-col items-start text-left group"
            >
              <div className="relative w-full ava-radius-md overflow-hidden mb-3" style={{ aspectRatio: '3/4', backgroundColor: s.tint }}>
                {s.image ? (
                  <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Icon name={s.icon} size={40} color={s.color} />
                  </div>
                )}
                <div
                  className="absolute top-3 left-3 w-8 h-8 rounded-full flex items-center justify-center bg-white"
                  style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
                >
                  <Icon name={s.icon} size={16} color={s.color} />
                </div>
              </div>
              <div className="ava-text-sm font-semibold" style={{ color: 'var(--text-dark)' }}>{s.title}</div>
              <div className="ava-text-xs mt-0.5" style={{ color: 'var(--text-light)' }}>{s.desc}</div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ────────────────────────────────────────────────────────────────────
   5. POPULAR HEALTH CONCERNS — icon grid
──────────────────────────────────────────────────────────────────── */
const DEFAULT_CONCERNS = [
  { icon: 'hormone', label: 'PCOS & PCOD' },
  { icon: 'heart', label: 'Menstrual Health' },
  { icon: 'baby', label: 'Infertility' },
  { icon: 'brain', label: 'Thyroid Disorders' },
  { icon: 'alert', label: 'Endometriosis' },
  { icon: 'baby', label: 'Pregnancy Care' },
  { icon: 'shield', label: 'Menopause Support' },
  { icon: 'brain', label: 'Mental Wellness' },
  { icon: 'heart', label: 'Breast Health' },
  { icon: 'hormone', label: 'Vaginal Health' },
];

export const PopularHealthConcerns = ({ heading = 'Popular Health Concerns', concerns = DEFAULT_CONCERNS, exploreHref = '/conditions' }) => {
  const navigate = useNavigate();
  return (
    <section className="w-full py-8 md:py-10" style={{ backgroundColor: 'var(--gray-100)' }}>
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="ava-heading-lg">{heading}</h2>
          <button onClick={() => navigate(exploreHref)} className="ava-text-sm font-semibold inline-flex items-center gap-1" style={{ color: 'var(--primary-color)' }}>
            Explore all conditions <Icon name="chevronRight" size={14} color="var(--primary-color)" />
          </button>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 ava-card-gap">
          {concerns.map((c) => (
            <button
              key={c.label}
              onClick={() => navigate(`/search?q=${encodeURIComponent(c.label)}`)}
              className="flex flex-col items-center gap-2.5 bg-white ava-radius-md py-5 px-2 transition-transform hover:-translate-y-0.5"
              style={{ border: '1px solid var(--gray-200, #e5e7eb)' }}
            >
              <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--ava-color-primary-50)' }}>
                <Icon name={c.icon} size={20} color="var(--primary-color)" />
              </div>
              <span className="ava-text-xs font-medium text-center" style={{ color: 'var(--text-dark)', lineHeight: 1.2 }}>{c.label}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ────────────────────────────────────────────────────────────────────
   6. STATS BANNER
──────────────────────────────────────────────────────────────────── */
const DEFAULT_STATS = [
  { icon: 'doctor', value: '500+', label: 'Expert Doctors On Platform' },
  { icon: 'clipboard', value: '1M+', label: 'Health Assessments Completed' },
  { icon: 'users', value: '50K+', label: 'Women Trust Us Every Month' },
  { icon: 'heart', value: '98%', label: 'Patient Satisfaction Rate' },
  { icon: 'shield', value: '100%', label: 'Secure & Private, Your Data, Our Priority' },
];

export const StatsBanner = ({ stats = DEFAULT_STATS }) => (
  <section className="w-full py-8 md:py-10" style={{ backgroundColor: 'var(--primary-color)' }}>
    <div className="container mx-auto px-4 md:px-8">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-4">
        {stats.map((s) => (
          <div key={s.label} className="flex items-center gap-3">
            <Icon name={s.icon} size={24} color="#fff" />
            <div>
              <div className="text-white font-bold" style={{ fontSize: '1.35rem', lineHeight: 1.1 }}>{s.value}</div>
              <div className="ava-text-xs" style={{ color: 'rgba(255,255,255,0.8)' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ────────────────────────────────────────────────────────────────────
   7. BOTTOM CTA — "Care that understands you."
──────────────────────────────────────────────────────────────────── */
export const BottomCTASection = ({
  headingStrong = 'Care that understands you.',
  headingAccent = 'Technology that empowers you.',
  description = 'Begin your health journey with Avexa OneCare today.',
  image,
  imageAlt = 'Avexa OneCare doctor',
  primaryCta = { label: 'Start a Conversation', action: 'start-conversation' },
  secondaryCta = { label: 'Book Appointment', href: '/book-appointment' },
}) => {
  const navigate = useNavigate();

  const handlePrimary = () => {
    if (primaryCta.action === 'start-conversation') {
      document.getElementById('ai-chat-input')?.focus();
      return;
    }
    navigate(primaryCta.href || '/book-appointment');
  };

  return (
    <section className="w-full py-10 md:py-14" style={{ backgroundColor: 'var(--ava-color-primary-50)' }}>
      <div className="container mx-auto px-4 md:px-8">
        <div
          className="relative ava-radius-md overflow-hidden flex flex-col md:flex-row items-center gap-6 md:gap-10 px-6 md:px-10 py-8 md:py-10"
          style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
        >
          <div className="max-w-[420px]">
            <h2 className="ava-heading-lg mb-1">
              <span className="block" style={{ color: 'var(--text-dark)' }}>{headingStrong}</span>
              <span className="block" style={{ color: 'var(--secondary-color)' }}>{headingAccent}</span>
            </h2>
            <p className="ava-text-body mt-3">{description}</p>
          </div>

          {image && (
            <div className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden flex-shrink-0" style={{ border: '4px solid #fff', boxShadow: '0 6px 20px rgba(0,0,0,0.1)' }}>
              <img src={image} alt={imageAlt} className="w-full h-full object-cover" draggable={false} />
            </div>
          )}

          <div className="flex flex-col gap-3 flex-1 w-full md:w-auto">
            <div className="ava-text-subheading">Need help right now?</div>
            <p className="ava-text-body" style={{ maxWidth: 320 }}>
              Chat with our AI Care Navigator or book an appointment.
            </p>
            <div className="flex flex-wrap gap-3">
              <button className="btn-primary" onClick={handlePrimary}>{primaryCta.label}</button>
              <button className="btn-transparent" onClick={() => navigate(secondaryCta.href || '/book-appointment')}>
                {secondaryCta.label}
              </button>
            </div>
          </div>

          <button
            onClick={handlePrimary}
            aria-label="Start a conversation"
            className="hidden md:flex absolute bottom-6 right-6 w-14 h-14 rounded-full items-center justify-center"
            style={{ backgroundColor: 'var(--primary-color)', boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}
          >
            <Icon name="sparkles" size={22} color="#fff" />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Composed page — drop straight into Home.jsx if you want everything
──────────────────────────────────────────────────────────────────── */
const AICareHome = (props) => (
  <>
    <AIHeroSection {...props.hero} />
    <PopularConversations {...props.conversations} />
    <CareJourney {...props.journey} />
    <LifeStages {...props.stages} />
    <PopularHealthConcerns {...props.concerns} />
    <StatsBanner {...props.stats} />
    <BottomCTASection {...props.cta} />
  </>
);

export default AICareHome;
